import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, firstValueFrom, from, map, switchMap, throwError } from 'rxjs';
import { API_BASE_URL } from '../auth/api.config';
import { normalizeApiError } from '../http/api-error';
import {
  loadApiOperations,
  withOperation,
  GeneratedApiClient,
  GeneratedApiTransport,
  ApiOperationDefinition,
  OperationHeaders,
  OperationId,
  OperationPathParams,
  OperationQuery,
  OperationRequest,
  OperationSuccessResponse,
  expandOperationPath,
} from './generated';

@Injectable({ providedIn: 'root' })
class AngularGeneratedApiTransport implements GeneratedApiTransport {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL).replace(/\/$/, '');

  request<Id extends OperationId>(
    operationId: Id,
    definition: ApiOperationDefinition,
    request: OperationRequest<Id>,
  ): Promise<OperationSuccessResponse<Id>> {
    const { url, options } = this.prepare(operationId, definition, request);

    if (definition.responseType === 'blob') {
      return firstValueFrom(
        this.withNormalizedErrors(
          // A blob request receives its *error* body as a blob too, so the
          // envelope inside it is never parsed and everything the server said
          // about the refusal is lost — the message, the request id, and the
          // `meta.code` that is sometimes the only thing separating two
          // refusals sharing a status. A report export answers 409 for both
          // "still generating" and "the stored file no longer matches its
          // checksum", and a client that cannot read the code cannot tell a
          // reader to wait from telling them the file is corrupt.
          this.http.request(definition.method, url, { ...options, responseType: 'blob' })
            .pipe(catchError((error: unknown) => this.readBlobError(error))),
        ).pipe(map((response) => response as unknown as OperationSuccessResponse<Id>)),
      );
    }

    if (definition.responseType === 'text') {
      return firstValueFrom(
        this.withNormalizedErrors(
          this.http.request(definition.method, url, { ...options, responseType: 'text' }),
        ).pipe(map((response) => response as unknown as OperationSuccessResponse<Id>)),
      );
    }

    return firstValueFrom(
      this.withNormalizedErrors(
        this.http.request<OperationSuccessResponse<Id>>(definition.method, url, options),
      ),
    );
  }

  /**
   * Re-read a blob error body as the JSON envelope it actually is.
   *
   * Only for a body that claims to be JSON: a genuinely binary error response
   * is left exactly as it arrived rather than being coerced into a shape it
   * does not have.
   */
  private readBlobError(error: unknown): Observable<never> {
    if (!(error instanceof HttpErrorResponse) || !(error.error instanceof Blob)) {
      return throwError(() => error);
    }

    const blob = error.error;

    if (!blob.type.includes('json')) {
      return throwError(() => error);
    }

    return from(blob.text()).pipe(
      switchMap((text) => {
        let parsed: unknown = null;

        try {
          parsed = JSON.parse(text) as unknown;
        } catch {
          // Not the envelope after all; the original error is the honest one.
          return throwError(() => error);
        }

        return throwError(() => new HttpErrorResponse({
          error: parsed,
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined,
        }));
      }),
    );
  }

  /**
   * The same call, with the status code kept.
   *
   * `request` returns the parsed body, which is all that almost every
   * operation carries — but not all of them. Posting a goods receipt answers
   * **201 when it posted and 200 when it replayed an earlier identical post**,
   * with the same body either way, and that status is the only thing telling a
   * receiving clerk whether the delivery in front of them was just booked in or
   * had already been booked in. Dropping it turns an idempotent endpoint into
   * one where receiving twice looks exactly like receiving once.
   *
   * JSON only. A blob or text operation has no such distinction to preserve,
   * and giving this method a second response shape to handle would be an
   * invitation to use it where `request` is correct.
   */
  requestWithStatus<Id extends OperationId>(
    operationId: Id,
    definition: ApiOperationDefinition,
    request: OperationRequest<Id>,
  ): Promise<StatusedResponse<Id>> {
    if (definition.responseType !== 'json') {
      throw new TypeError(`Operation "${operationId}" does not answer with JSON.`);
    }
    const { url, options } = this.prepare(operationId, definition, request);

    return firstValueFrom(
      this.withNormalizedErrors(
        this.http.request<OperationSuccessResponse<Id>>(definition.method, url, {
          ...options,
          observe: 'response',
        }),
      ).pipe(
        map((response) => ({
          status: response.status,
          // `null` only for 204, which no operation using this declares.
          body: response.body as OperationSuccessResponse<Id>,
        })),
      ),
    );
  }

  private prepare<Id extends OperationId>(
    operationId: Id,
    definition: ApiOperationDefinition,
    request: OperationRequest<Id>,
  ): { url: string; options: { headers: HttpHeaders; params: HttpParams; body?: unknown } } {
    const parts = request as OperationRequest<Id> & {
      readonly pathParams?: OperationPathParams<Id>;
      readonly query?: OperationQuery<Id>;
      readonly headers?: OperationHeaders<Id>;
      readonly body?: unknown;
    };
    const path = expandOperationPath(
      operationId,
      definition.path,
      (parts.pathParams ?? {}) as OperationPathParams<Id>,
    );
    const hasBody = Object.prototype.hasOwnProperty.call(parts, 'body');

    return {
      url: `${this.apiBaseUrl}/api/v1${path}`,
      options: {
        headers: this.headers(parts.headers, definition.responseMediaType),
        params: this.queryParams(parts.query),
        ...(hasBody ? { body: this.requestBody(definition.requestMediaType, parts.body) } : {}),
      },
    };
  }

  private requestBody(mediaType: string | null, value: unknown): unknown {
    return mediaType === 'multipart/form-data' ? this.formData(value) : value;
  }

  private formData(value: unknown): FormData {
    if (value instanceof FormData) {
      return value;
    }
    if (!value || typeof value !== 'object' || value instanceof Blob || Array.isArray(value)) {
      throw new TypeError('A multipart request body must be an object or FormData.');
    }

    const formData = new FormData();
    for (const [name, item] of Object.entries(value as Record<string, unknown>)) {
      this.appendFormValue(formData, name, item);
    }

    return formData;
  }

  private appendFormValue(formData: FormData, name: string, value: unknown): void {
    if (value === undefined || value === null) {
      return;
    }
    if (value instanceof Blob) {
      const fileName = typeof File !== 'undefined' && value instanceof File ? value.name : 'blob';
      formData.append(name, value, fileName);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item, index) => this.appendFormValue(formData, `${name}[${index}]`, item));
      return;
    }
    if (typeof value === 'object') {
      for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
        this.appendFormValue(formData, `${name}[${key}]`, item);
      }
      return;
    }

    formData.append(name, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
  }

  private withNormalizedErrors<T>(source: Observable<T>): Observable<T> {
    return source.pipe(catchError((error: unknown) => throwError(() => normalizeApiError(error))));
  }

  private headers<Id extends OperationId>(
    values: OperationHeaders<Id> | undefined,
    responseMediaType: string | null,
  ): HttpHeaders {
    let headers = new HttpHeaders();

    if (responseMediaType) {
      headers = headers.set('Accept', responseMediaType);
    }

    for (const [name, value] of Object.entries((values ?? {}) as Record<string, unknown>)) {
      if (value !== undefined && value !== null) {
        headers = headers.set(name, String(value));
      }
    }

    return headers;
  }

  private queryParams<Id extends OperationId>(values: OperationQuery<Id> | undefined): HttpParams {
    let params = new HttpParams();

    for (const [name, rawValue] of Object.entries((values ?? {}) as Record<string, unknown>)) {
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        continue;
      }

      for (const value of Array.isArray(rawValue) ? rawValue : [rawValue]) {
        params = params.append(name, String(value));
      }
    }

    return params;
  }
}

/** A response whose status carries meaning the body does not. */
export interface StatusedResponse<Id extends OperationId> {
  readonly status: number;
  readonly body: OperationSuccessResponse<Id>;
}

@Injectable({ providedIn: 'root' })
export class OpenApiClient {
  private readonly transport = inject(AngularGeneratedApiTransport);
  private readonly client = new GeneratedApiClient(this.transport);

  constructor() {
    // Start fetching the operation table as the application boots, in parallel
    // with everything else it is doing, so that it is in memory before a screen
    // asks for anything and no request ever waits on it. `withOperation` still
    // awaits it if a request does get there first; this only stops that from
    // being the ordinary case.
    void loadApiOperations();
  }

  request<Id extends OperationId>(
    operationId: Id,
    request: OperationRequest<Id>,
  ): Promise<OperationSuccessResponse<Id>> {
    return this.client.request(operationId, request);
  }

  /**
   * For the few operations whose status is part of the answer.
   *
   * Only one exists today — posting a goods receipt, whose 201 and 200 mean
   * "posted" and "already posted". Reach for `request` everywhere else.
   */
  requestWithStatus<Id extends OperationId>(
    operationId: Id,
    request: OperationRequest<Id>,
  ): Promise<StatusedResponse<Id>> {
    return withOperation(operationId, (definition) =>
      this.transport.requestWithStatus(operationId, definition, request));
  }
}
